import React, { useEffect, useRef, useState } from "react";
import styles from "./HeroPlate.module.css";
import CustomButton from "./CustomButton";

type ImageMedia = {
  kind: "image";
  src: string;
  alt?: string;
  objectPositionY?: string;
};

export type Media = ImageMedia;

type CTA = {
  text: string;
  to: string;
  variant?: "filled" | "outline";
  size?: "small" | "medium" | "large";
};

export type HeroVariant =
  | "plate-left"
  | "plate-right"
  | "diagonal-splitleft"
  | "diagonal-splitright";

type CSSVars = {
  "--hero-h"?: string;
  "--img-pos-y"?: string;
  "--plate-max-w"?: string;
  "--safe-top"?: string;
  "--container-max"?: string;
  "--container-pad"?: string;
};

export type HeroPlateProps = {
  media: Media;
  title: string;
  kicker?: string;
  lead?: string;
  ctas?: CTA[];
  variant?: HeroVariant;
  tone?: "dark" | "light";
  blur?: boolean;
  height?: string;
  aspectRatio?: string;
  maxPlateWidth?: number;
  topSafeOffsetVar?: string;
  imgPosY?: string;
  className?: string;
  parallax?: boolean;
  strongSplit?: boolean;
  vignette?: boolean;
  grain?: boolean;
  compactMobile?: boolean;
};

const HeroPlate: React.FC<HeroPlateProps> = ({
  media,
  title,
  kicker,
  lead,
  ctas,
  variant = "plate-left",
  tone = "dark",
  blur = true,
  height = "clamp(56vh, 68vh, 82vh)",
  aspectRatio = "16 / 9",
  maxPlateWidth = 640,
  topSafeOffsetVar,
  imgPosY = "40%",
  className = "",
  parallax = true,
  strongSplit = false,
  vignette = true,
  grain = true,
  compactMobile = true,
}) => {
  const [reduced, setReduced] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    const apply = () => setReduced(!!mq.matches);
    apply();
    mq.addEventListener?.("change", apply);
    return () => mq.removeEventListener?.("change", apply);
  }, []);

  const styleVars: React.CSSProperties & CSSVars = {
    "--hero-h": height,
    "--img-pos-y": media.objectPositionY ?? imgPosY,
    "--plate-max-w": `${maxPlateWidth}px`,
  };
  if (topSafeOffsetVar) styleVars["--safe-top"] = `var(${topSafeOffsetVar})`;

  const hostRef = useRef<HTMLElement | null>(null);
  const mediaRef = useRef<HTMLImageElement | null>(null);
  useEffect(() => {
    if (!parallax || reduced) return;
    const el = hostRef.current;
    if (!el) return;
    let raf = 0;
    const onScroll = () => {
      const rect = el.getBoundingClientRect();
      const k = 0.12;
      const y = -rect.top * k;
      mediaRef.current?.style.setProperty("--parallax-y", `${y.toFixed(1)}px`);
    };
    const tick = () => {
      onScroll();
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [parallax, reduced]);

  const showPlate = kicker || title || lead || (ctas && ctas.length);
  const toneClass = styles[`tone_${tone}`];
  const variantClass = styles[`v_${variant}`];

  return (
    <section
      ref={hostRef as React.Ref<HTMLElement>}
      className={[
        styles.hero,
        styles.fullBleed,
        variantClass,
        toneClass,
        blur ? styles.hasBlur : styles.noBlur,
        strongSplit ? styles.strongSplit : "",
        vignette ? styles.vignette : "",
        grain ? styles.grain : "",
        className,
      ].join(" ")}
      style={styleVars}
      aria-label="Hero"
      data-compact={compactMobile ? "1" : "0"}
      data-reduced={reduced ? "1" : "0"}
    >
      <img
        ref={mediaRef as React.Ref<HTMLImageElement>}
        className={styles.media}
        src={media.src}
        alt={media.alt ?? ""}
        decoding="async"
        loading="eager"
        style={{ aspectRatio }}
      />

      <div className={styles.diagonalLayer} aria-hidden="true" />
      <div className={styles.fadeTop} aria-hidden="true" />
      <div className={styles.fadeBottom} aria-hidden="true" />

      {variant === "diagonal-splitleft" || variant === "diagonal-splitright" ? (
        <div className={styles.splitWrap}>
          <div className={styles.splitPlate}>
            {kicker && <p className={styles.kicker}>{kicker}</p>}
            <h1 className={styles.splitTitle}>{title}</h1>
            {lead && <p className={styles.splitLead}>{lead}</p>}
            {ctas?.length ? (
              <div
                className={[
                  styles.ctaRow,
                  variant === "diagonal-splitright" ? styles.ctaEnd : styles.ctaStart,
                  styles.hideCtasNarrow,
                ].join(" ")}
              >
                {ctas.map((c) => (
                  <CustomButton
                    key={`${c.text}-${c.to}`}
                    to={c.to}
                    text={c.text}
                    variant={c.variant ?? "filled"}
                    size={c.size ?? "small"}
                  />
                ))}
              </div>
            ) : null}
          </div>
        </div>
      ) : (
        showPlate && (
          <div className={styles.plateWrap}>
            <div className={styles.plate}>
              {kicker && <p className={styles.kicker}>{kicker}</p>}
              <h1 className={styles.title}>{title}</h1>
              {lead && <p className={styles.lead}>{lead}</p>}
              {ctas?.length ? (
                <div className={[styles.ctaRow, styles.hideCtasNarrow].join(" ")}>
                  {ctas.map((c) => (
                    <CustomButton
                      key={`${c.text}-${c.to}`}
                      to={c.to}
                      text={c.text}
                      variant={c.variant ?? "filled"}
                      size={c.size ?? "small"}
                    />
                  ))}
                </div>
              ) : null}
            </div>
          </div>
        )
      )}
    </section>
  );
};

export default HeroPlate;
